"use client"

import { useCart } from "@lib/context/cart-context"
import { User } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import SignOutButton from "./sign-out-button"

const menuLinks = [
  { label: "My Account", href: "/account" },
  { label: "My Orders", href: "/account/orders" },
  { label: "Saved Addresses", href: "/account/addresses" },
]

/**
 * Client component on purpose (2026-07-02): the old server version called
 * `retrieveCustomer()` from the public nav, which made EVERY public page
 * request-scoped (`no-store`, ISR dead). The customer now comes from the
 * existing client CartProvider state (fetched after hydration), so SSR renders
 * the guest dropdown and personalizes a moment later — same pattern as the
 * cart button. Do NOT reintroduce a server customer/cookie read here.
 */
export default function AccountMenu() {
  const { customer } = useCart()

  return (
    <div className="group/acct relative">
      {/* Trigger */}
      <LocalizedClientLink
        href="/account"
        aria-label="Account"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#1A1A1A] transition-colors hover:bg-gray-100"
      >
        <User className="h-5 w-5" aria-hidden="true" />
      </LocalizedClientLink>

      {/* Invisible bridge so dropdown doesn't close on gap */}
      <div className="invisible absolute right-0 top-full h-3 w-48 group-hover/acct:visible" />

      {/* Dropdown panel */}
      <div className="invisible absolute right-0 top-[calc(100%+12px)] z-50 w-52 border border-[#E5E5E5] bg-white opacity-0 shadow-[0_16px_40px_-12px_rgba(26,26,26,0.18)] transition-all duration-150 group-hover/acct:visible group-hover/acct:opacity-100">

        {customer ? (
          <>
            {/* Customer name */}
            <div className="border-b border-[#f0ece4] px-4 py-3">
              <p className="alif-serif text-[15px] font-medium text-[#1A1A1A] leading-snug">
                {customer.first_name
                  ? `${customer.first_name}${customer.last_name ? " " + customer.last_name : ""}`
                  : customer.email}
              </p>
              {customer.first_name && (
                <p className="mt-0.5 text-[11px] text-[#999] truncate">{customer.email}</p>
              )}
            </div>

            {/* Nav links */}
            <div className="py-1.5">
              {menuLinks.map(({ label, href }) => (
                <LocalizedClientLink
                  key={href}
                  href={href}
                  className="block px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[#444] transition-colors hover:bg-[#f9f7f4] hover:text-[#1A1A1A]"
                >
                  {label}
                </LocalizedClientLink>
              ))}
            </div>

            {/* Divider + sign out */}
            <div className="border-t border-[#f0ece4]">
              <SignOutButton />
            </div>
          </>
        ) : (
          <>
            <div className="border-b border-[#f0ece4] px-4 py-3">
              <p className="alif-serif text-[14px] font-medium text-[#1A1A1A]">Welcome</p>
              <p className="mt-0.5 text-[11px] text-[#999]">Sign in to your account</p>
            </div>
            <div className="py-1.5">
              <LocalizedClientLink
                href="/account"
                className="block px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[#444] transition-colors hover:bg-[#f9f7f4] hover:text-[#1A1A1A]"
              >
                Sign In
              </LocalizedClientLink>
              <LocalizedClientLink
                href="/account?view=register"
                className="block px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-[#3D5240] transition-colors hover:bg-[#f9f7f4]"
              >
                Create Account
              </LocalizedClientLink>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
